import React, { useEffect, useState } from "react";
import { useGetCompanies, useGetCompanyLocations, useGetDepartmentUserMappings } from "../../../../backend/masters";
import { DEFAULT_OPTIONS_PAYLOAD } from "../../../common/Table";
import FormRenderer, { ComponentMapper, FormTemplate, componentTypes } from "../../../common/FormRenderer";
import { validatorTypes } from "@data-driven-forms/react-form-renderer";
import { Modal } from "react-bootstrap";
import { getUserDetails } from "../../../../backend/auth";
import { downloadFileContent, getValue } from "../../../../utils/common";
import { getAllComplianceActivies, useExportComplianceReport } from "../../../../backend/compliance";
import { MONTHS_ENUM } from "../../../common/Constants";
import { toast } from "react-toastify";
import { ERROR_MESSAGES } from "../../../../utils/constants";
import PageLoader from "../../../shared/PageLoader";
import dayjs from "dayjs";

function locationOptionLabel({ label, location }: any) {
  return (
    <div className="d-flex flex-column">
      <div>{label}</div>
      {
        Boolean(location) &&
        <>
          <div className="text-sm fw-bold">{location.city.label} | {location.state.label}</div>
        </>
      }
    </div>
  )
}

export default function ComplianceReport({ onCancel }: any) {
    const [filters, setFilters] = useState<any>({})
    const [companies, setCompanies] = useState<any[]>([]);
    const [associateCompanies, setAssociateCompanies] = useState<any[]>();
    const [locations, setLocations] = useState<any[]>();
    const { departmentUsers, isFetching: fetchingCompanies }: any = useGetDepartmentUserMappings({
      ...DEFAULT_OPTIONS_PAYLOAD,
      filters: {
        user_id_eq:  getUserDetails().user_id
      }
    }, true);

    const { companies: acs, isFetching: fetchingAcs } = useGetCompanies({
      ...DEFAULT_OPTIONS_PAYLOAD,
      filters: {
        is_parent_eq: false,
        parent_company_id_eq: (filters.company || {}).value
      }
    }, enableAcs());

    const { locations: locs, isFetching: fetchingLocs } = useGetCompanyLocations({
        ...DEFAULT_OPTIONS_PAYLOAD,
        filters: {
          company_id_eq: (filters.associate_company || {}).value
        },
        include: {
          location: {
            include: {
              city: {
                include: ['state']
              }
            }
          }
        },
        includes: {
          location: {
            city: ['state']            
          }
        },
        sorts: ['location_name asc']
    }, enableLocations());
    
    const getAuditReportFileName = (response: any) => {
      const { company, associate_company, location, monthYear } = filters;
      const ac = acs.find(
        (item: any) => item.id === associate_company.value
      ).code;
      const comp = getValue(
        departmentUsers.find(
          (item: any) =>
            getValue(item, "department.vertical.company.id") ===
            company.value
        ) || {},
        "department.vertical.company.code"
      );
      const loc = getValue(location, "location.code");
      let monthYearString: any;
      if (Array.isArray(monthYear)) {
        if(monthYear.length === 2) {
          monthYearString = MONTHS_ENUM[new Date(monthYear[0]).getMonth()] + '_' + new Date(monthYear[0]).getFullYear() + '_' + MONTHS_ENUM[new Date(monthYear[1]).getMonth()] + '_' + new Date(monthYear[1]).getFullYear();
        } else if(monthYear.length === 1) {
          monthYearString = MONTHS_ENUM[new Date(monthYear[0]).getMonth()] + '_' + new Date(monthYear[0]).getFullYear();
        }
      } else {
        monthYearString = MONTHS_ENUM[new Date(monthYear).getMonth()] + '_' + new Date(monthYear).getFullYear();
      }
      const fileNameChunks = [
        "Compliance_Dashboard_Report",
        comp,
        ac,
        loc,
        monthYearString
      ];
      const fileExt = response.headers['content-type'].split('/')[1] || 'pdf';
      const fileName = `${fileNameChunks.join('_')}.${fileExt}`;
      console.log(fileName)
      return fileName;
    }
    
    const { exportReport, exporting } = useExportComplianceReport(
      (response: any) => {
        if (response) {
          downloadFileContent({
            name: getAuditReportFileName(
              response
            ),
            type: response.headers["content-type"],
            content: response.data,
          });
        }
      },
      (error: any) => {
        console.log(error)
        toast.error(ERROR_MESSAGES.DEFAULT);
      }
    );
    /*
    const { exportReport, exporting } = useExportComplianceReport((response: any) => {
        if (response) {
            
            downloadFileContent({
                name: fileName,
                type: response.headers['content-type'],
                content: response.data
            });
        }
    }, () => {
        toast.error(ERROR_MESSAGES.DEFAULT)
    })
    */
    function enableAcs() {
        const { company } = filters;
        return Boolean(company && company.value);
    }

    function enableLocations() {
        const { associate_company } = filters;
        return Boolean(associate_company && associate_company.value);
    }

    const schema = {
        fields: [
            {
                component: componentTypes.MONTH_PICKER,
                name: 'monthYear',
                label: 'Month & Year',
                className: 'grid-col-50',
                validate: [
                    { type: validatorTypes.REQUIRED }
                ],
                range: true,
                onChange: (date: any) => {
                    setFilters({
                        ...filters,
                        monthYear: date ? date : undefined
                    })
                }
            },
            {
                component: componentTypes.SELECT,
                name: 'company',
                label: 'Company',
                validate: [
                    { type: validatorTypes.REQUIRED }
                ],
                options: companies,
                onChange: (company: any) => {
                    setAssociateCompanies([]);
                    setLocations([]);
                    setFilters({
                        ...filters,
                        company,
                        associate_company: null,
                        location: null
                    });
                }
            },
            {
                component: componentTypes.SELECT,
                name: 'associate_company',
                label: 'Associate Company',
                validate: [
                    { type: validatorTypes.REQUIRED }
                ],
                options: associateCompanies,
                onChange: (associate_company: any) => {
                    setFilters({
                        ...filters,
                        associate_company,
                        location: null
                    });
                }
            },
            {
                component: componentTypes.SELECT,
                name: 'location',
                label: 'Location',
                options: locations,
                onChange: (location: any) => {
                    setFilters({
                        ...filters,
                        location
                    });
                },
                formatOptionLabel: locationOptionLabel,
            }
        ]
    };

    function handleSubmit(data: any) {
        const {
            company, associate_company, location, monthYear
        } = data;
        const _filters:any = {};

        let fromDate, toDate;
        if (Array.isArray(monthYear)) {
            fromDate = new Date(monthYear[0]);
            fromDate.setDate(1);
            toDate = new Date(monthYear[1] || monthYear[0]);
            toDate = new Date(toDate.getFullYear(), toDate.getMonth() + 1, 0);
        } else {
            fromDate = new Date(monthYear);
            fromDate.setDate(1);
            toDate = new Date(monthYear);
            toDate = new Date(toDate.getFullYear(), toDate.getMonth() + 1, 0);
        }

        if (fromDate) {
            _filters.from_date = dayjs(fromDate).local().format();
        }
        if (toDate) {
            _filters.to_date = dayjs(toDate).local().format();
        }

        if (company && company.value) {
          _filters.company_id_eq = company.value;
        }
        if (associate_company && associate_company.value) {
          _filters.associate_company_id_eq = associate_company.value
        }
        if (location && location.value) {
          _filters.location_id_eq = location.value
        }
        const payload = {
            filters: _filters,
        };
        getAllComplianceActivies({
            ...payload,
            page: 1
        }).then(({ data }: any) => {
          if (data.meta.total_count > 0) {
            exportReport(payload);
          } else {
            toast.warn("There are no compliance activites available for provided filter.");
          }
        })
    }

    useEffect(() => {
        if (!fetchingCompanies) {
            const comps: any = {};
            departmentUsers.forEach((usr: any) => {
                const company = getValue(usr, 'department.vertical.company');
                if (!comps[company.id]) {
                    comps[company.id] = company;
                }
            });
            setCompanies(Object.values(comps).map(({ id, name }: any) => {
                return { value: id, label: name }
            }))
        }
    }, [fetchingCompanies])

    useEffect(() => {
        if (!fetchingLocs && locs) {
            setLocations((locs || []).map(({ location }: any) => {
                const { id, name, code, city } = location;
                const { state } = city;
                return {
                    id, name, code,
                    city: { code: city.code, label: city.name, value: city.id },
                    state: { code: state.code, label: state.name, value: state.id }
                }
            }));
        }
    }, [fetchingLocs]);

    useEffect(() => {
        if (!fetchingCompanies && acs) {
            setLocations([]);
            setAssociateCompanies(acs.map(({ id, name }: any) => {
                return { value: id, label: name }
            }));
        }
    }, [fetchingAcs]);
    return (
        <>
            <Modal show={true} backdrop="static" animation={false} dialogClassName="drawer no-footer" size="lg">
                <Modal.Header closeButton={true} onHide={onCancel}>
                    <Modal.Title className="bg">Generate Compliance Report</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <FormRenderer FormTemplate={FormTemplate}
                        initialValues={{
                            submitBtnText: 'Generate Report',
                            ...filters
                        }}
                        componentMapper={ComponentMapper}
                        schema={schema}
                        onSubmit={handleSubmit}
                    />
                </Modal.Body>
            </Modal>
            {
                exporting && <PageLoader>{'Preparing Data'}</PageLoader>
            }
        </>
    )
}
